import { useEffect, useState } from "react";
import { adminApi } from "../../services/adminApi";
import { Button } from "../common/Button";
import { Badge } from "../common/Badge";
import { ConfirmDialog } from "../common/ConfirmDialog";

/**
 * Undo one audited change, field by field (#315).
 *
 * PREVIEW FIRST, ALWAYS. #315 asks for "selective, row by row,
 * reviewed", and the thing worth reviewing is not the list of fields
 * that will go back -- it is the list that will NOT. V020 and V031
 * replace fourteen field names with the literal string [REDACTED]
 * before anything reaches the audit log, so an address's before-state
 * holds that word and not the address. The server refuses to write it
 * back; this panel's job is making sure the operator KNOWS, because
 * they are the one who has to tell the customer those fields are still
 * wrong.
 *
 * A refusal is shown as an ANSWER, not an error. An operator who asks
 * "can this be undone" and is told "no, because the honey pool is a
 * ledger and this needs a correcting entry instead" got their question
 * answered. Rendering that as a failure would suggest something went
 * wrong when the system worked exactly as designed.
 *
 * Fields are individually deselectable. The common case is restoring
 * everything, so they all start ticked -- but a change that touched six
 * fields is often only wrong in two, and putting back the other four
 * would overwrite work somebody did legitimately in between.
 */
export function RevertPanel({ logId, onDone }) {
  const [plan, setPlan] = useState(null);
  const [refusal, setRefusal] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPlan(null);
    setRefusal(null);
    setResult(null);
    setError(null);
    adminApi.previewRevert(logId)
      .then((r) => {
        if (cancelled) return;
        if (r.revertable === false) {
          setRefusal(r.reason);
          return;
        }
        setPlan(r);
        setChosen(r.restorable.map((f) => f.field));
      })
      .catch((err) => !cancelled && setError(err.apiMessage || "Could not read that entry."));
    return () => { cancelled = true; };
  }, [logId]);

  function toggle(field) {
    setChosen((c) => (c.includes(field) ? c.filter((f) => f !== field) : [...c, field]));
  }

  async function doRevert() {
    setBusy(true);
    try {
      const r = await adminApi.applyRevert(logId, chosen);
      setResult(r);
      setConfirming(false);
      onDone?.();
    } catch (err) {
      // The server's message names what it would not do. Replacing it
      // with something generic would throw away the only part an
      // operator can act on.
      setError(err.apiMessage || "The revert was refused.");
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  if (error) return <p style={{ color: "var(--color-danger)" }}>{error}</p>;

  if (result) {
    return (
      <div>
        <p>
          <Badge tone="success">Reverted</Badge>{" "}
          Put back: <strong>{result.restored.join(", ")}</strong>
        </p>
        {result.refused.length > 0 && (
          /* Repeated AFTER the fact as well as before. The operator
             closing the ticket needs to tell the customer which fields
             are still wrong, and by then the preview is gone. */
          <p style={{ color: "var(--color-text-muted)" }}>
            Still wrong, and not recoverable:{" "}
            <strong>{result.refused.map((f) => f.field).join(", ")}</strong>. Their
            previous values were never recorded, so the customer has to supply them.
          </p>
        )}
      </div>
    );
  }

  if (refusal) {
    return (
      <div>
        <Badge tone="neutral">Cannot be reverted</Badge>
        <p style={{ marginBottom: 0 }}>{refusal}</p>
      </div>
    );
  }

  if (!plan) return <p style={{ color: "var(--color-text-muted)" }}>Reading the entry…</p>;

  return (
    <div>
      {plan.already_reverted && (
        <p>
          <Badge tone="warning">Already reverted</Badge>{" "}
          This entry was reverted on{" "}
          {new Date(plan.already_reverted.reverted_date).toLocaleString()}. Doing it
          again will put the same values back over anything changed since.
        </p>
      )}

      <p style={{ marginTop: 0 }}>
        <code>{plan.table}</code> record <code>{plan.record_id}</code>, changed by user{" "}
        <strong>{plan.changed_by}</strong> on {new Date(plan.changed_at).toLocaleString()}.
      </p>

      {plan.restorable.length > 0 ? (
        <ul style={{ listStyle: "none", padding: 0, margin: "var(--space-3) 0" }}>
          {plan.restorable.map((f) => (
            <li key={f.field} style={{ padding: "2px 0" }}>
              <label style={{ display: "flex", gap: "var(--space-2)", alignItems: "baseline" }}>
                <input
                  type="checkbox"
                  checked={chosen.includes(f.field)}
                  onChange={() => toggle(f.field)}
                />
                <span>
                  <strong>{f.field}</strong>{" "}
                  <code>{JSON.stringify(f.from)}</code> → <code>{JSON.stringify(f.to)}</code>
                </span>
              </label>
            </li>
          ))}
        </ul>
      ) : (
        <p>Nothing on this entry can be put back.</p>
      )}

      {plan.refused.length > 0 && (
        /* BEFORE the button, not after. An operator who reverts and then
           discovers the address was not restored has already told the
           customer it was. */
        <div style={{
          borderLeft: "3px solid var(--color-warning)",
          padding: "var(--space-2) var(--space-3)",
          margin: "var(--space-3) 0",
        }}>
          <strong>Cannot be restored:</strong>{" "}
          {plan.refused.map((f) => f.field).join(", ")}.
          <p style={{ margin: "var(--space-1) 0 0", color: "var(--color-text-muted)" }}>
            The audit log redacts these fields, so their previous values were never
            recorded — the only thing available to write back is the word
            [REDACTED], which would destroy the real value while looking like it
            worked. The customer has to supply them.
          </p>
        </div>
      )}

      <Button
        variant="danger"
        disabled={busy || chosen.length === 0}
        onClick={() => setConfirming(true)}
      >
        Put back {chosen.length} field{chosen.length === 1 ? "" : "s"}
      </Button>

      <ConfirmDialog
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={doRevert}
        title="Put these values back?"
        confirmLabel="Revert now"
        danger
        busy={busy}
      >
        <p>
          This overwrites the current values in <code>{plan.table}</code> record{" "}
          <code>{plan.record_id}</code> with what they were before that change.
          Anything edited since — by anybody — is overwritten too.
        </p>
        <p>
          Fields: <strong>{chosen.join(", ")}</strong>
        </p>
      </ConfirmDialog>
    </div>
  );
}
